// defs
import { IAllocationType } from "@defs/Utilities";
import { IStepper } from "@components/Stepper/Stepper.props";
import { AllocationType, Scenario, Sim } from "@utils/appsync/schema/API";
import { INewSimulationState } from "@store/state/NewSimulation";
import {
    GL_SIMULATION_SIMULATION_INPUTS,
    GLSimulationSteppersType,
} from "@pages/simulations/NewSimulation/containers/GLSimulation/constants";
import {
    OPEX_ALLOCATION_SIMULATION_INPUTS,
    OpexAllocationSteppersType,
} from "@pages/simulations/NewSimulation/containers/OpexAllocation/constants";

// constants
import { GL_SIMULATION_STEPPERS } from "@pages/simulations/NewSimulation/containers/GLSimulation/constants";
import { OPEX_ALLOCATION_STEPPERS } from "@pages/simulations/NewSimulation/containers/OpexAllocation/constants";

// helpers
import { createDeepCopy } from "@utils/index";

export function initializeNewSimulationState(
    allocationType: IAllocationType,
    existingScenarioList?: Array<Scenario>,
): Partial<INewSimulationState> {
    const newSimulationState = {};

    // update the readonly based upon simulation status
    newSimulationState["readOnly"] = false;

    // intialise the selected Scenario on route change or page load
    newSimulationState["selectedScenario"] = null;

    // Preserve existing scenarioList to avoid re-fetching
    if (existingScenarioList?.length) {
        newSimulationState["scenarioList"] = existingScenarioList;
    }

    switch (allocationType.key) {
        case AllocationType.opex_allocation:
            // initialise the steppers
            newSimulationState["simulationSteppers"] = createDeepCopy(OPEX_ALLOCATION_STEPPERS);

            // initialise the simulation inputs
            newSimulationState["simulationInputs"] = createDeepCopy(OPEX_ALLOCATION_SIMULATION_INPUTS);
            break;
        case AllocationType.gl_simulation:
            // initialise the steppers
            newSimulationState["simulationSteppers"] = createDeepCopy(GL_SIMULATION_STEPPERS);

            // initialise the simulation inputs
            newSimulationState["simulationInputs"] = createDeepCopy(GL_SIMULATION_SIMULATION_INPUTS);
            break;
    }

    // initialise the activeStepper
    newSimulationState["activeSimulationStepper"] = newSimulationState["simulationSteppers"][0];

    return newSimulationState;
}

function updateSimulationSteppers(
    simulationSteppers: Array<IStepper<OpexAllocationSteppersType | GLSimulationSteppersType>>,
    allocationType: IAllocationType,
    simulationDataConfig?: object,
): Array<IStepper<OpexAllocationSteppersType | GLSimulationSteppersType>> {
    switch (allocationType.key) {
        case AllocationType.opex_allocation:
            return simulationSteppers.map((opexAllocationStepper: IStepper<OpexAllocationSteppersType>) => {
                const stepper = { ...opexAllocationStepper };
                if (stepper.type === OpexAllocationSteppersType.ADD_DEFINIIONS_AND_MAPPINGS) {
                    if (simulationDataConfig?.["allocation_mapping"] || simulationDataConfig?.["allocation_definition"]) {
                        stepper.enableNext = true;
                    }
                }
                return stepper;
            });
        case AllocationType.gl_simulation:
            return simulationSteppers.map((glSimulationStepper: IStepper<GLSimulationSteppersType>) => {
                const stepper = { ...glSimulationStepper };
                if (stepper.type === GLSimulationSteppersType.INPUT_JOURNAL_ENTRIES) {
                    if (simulationDataConfig?.["manual_journal_entry"]) {
                        stepper.enableNext = true;
                    }
                }
                return stepper;
            });
    }
}

export function updateNewSimulationState(
    newSimulationState: INewSimulationState,
    allocationType: IAllocationType,
    simulationData?: Sim,
    scenarioList?: Array<Scenario>,
): Partial<INewSimulationState> {
    const updatedSimulationState = { ...newSimulationState, simulationData };
    const simulationDataConfig = JSON.parse(simulationData?.configs ?? "{}");

    // update the readonly based upon simulation status
    updatedSimulationState["readOnly"] = simulationData && simulationData.status_key !== "ACTIVE#SIM#CREATED";

    // update the simulation steppers
    updatedSimulationState["simulationSteppers"] = updateSimulationSteppers(
        updatedSimulationState["simulationSteppers"],
        allocationType,
        simulationDataConfig,
    );

    // update the selected Scenario on based upon simulation data
    if (simulationDataConfig.scenario && scenarioList?.length) {
        updatedSimulationState["selectedScenario"] = scenarioList.find(
            ({ scenario_name }) => scenario_name === simulationDataConfig.scenario,
        );
    }

    // initialise the activeStepper
    updatedSimulationState["activeSimulationStepper"] = updatedSimulationState["simulationSteppers"][0];

    // update the isSystemDefinitionAndMappingSelected in case of allocation type gl_simulation
    if (allocationType.key === AllocationType.gl_simulation) {
        if (simulationDataConfig?.["allocation_mapping"] || simulationDataConfig?.["allocation_definition"]) {
            updatedSimulationState["isSystemDefinitionAndMappingSelected"] = false;
        }
    }

    return updatedSimulationState;
}
